import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { LocalizedProcessingBadge, useT, type Translations } from '@/shared/i18n'
import { ToolHero } from '@/shared/ui/ToolHero'

type OutputMime = 'image/jpeg' | 'image/png' | 'image/webp'
type RotationDegrees = 0 | 90 | 180 | 270
type TransformAction = 'rotate-left' | 'rotate-right' | 'rotate-180' | 'flip-horizontal' | 'flip-vertical'
type RotateErrors = Translations['rotateImage']['errorsText']

interface LoadedImage {
  file: File
  url: string
  width: number
  height: number
  mime: OutputMime
}

interface TransformState {
  rotation: RotationDegrees
  flipHorizontal: boolean
  flipVertical: boolean
}

interface RotateResult {
  blob: Blob
  url: string
  width: number
  height: number
  mime: OutputMime
}

const UTILITY_KEY = 'rotate-image'
const MAX_FILE_SIZE = 50 * 1024 * 1024
const MAX_PIXELS = 36_000_000
const ALLOWED_TYPES: OutputMime[] = ['image/jpeg', 'image/png', 'image/webp']
const INITIAL_TRANSFORM: TransformState = {
  rotation: 0,
  flipHorizontal: false,
  flipVertical: false,
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function fileMime(file: File): OutputMime | null {
  const mime = file.type.toLowerCase()
  if (ALLOWED_TYPES.includes(mime as OutputMime)) {
    return mime as OutputMime
  }

  const name = file.name.toLowerCase()
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg'
  if (name.endsWith('.png')) return 'image/png'
  if (name.endsWith('.webp')) return 'image/webp'
  return null
}

function loadImage(src: string, errors: RotateErrors): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(errors.corrupt))
    img.src = src
  })
}

function blobFromCanvas(
  canvas: HTMLCanvasElement,
  mime: OutputMime,
  errors: RotateErrors,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error(errors.transformFailed))
          return
        }
        if (mime === 'image/webp' && blob.type !== 'image/webp') {
          reject(new Error(errors.unsupportedOutput))
          return
        }
        resolve(blob)
      },
      mime,
      mime === 'image/png' ? undefined : 0.95,
    )
  })
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function outputFilename(file: File, mime: OutputMime): string {
  const base = file.name.replace(/\.[^.]+$/, '') || 'image'
  const ext = mime === 'image/jpeg' ? 'jpg' : mime.replace('image/', '')
  return `${base}-rotated.${ext}`
}

function normalizeRotation(value: number): RotationDegrees {
  return (((value % 360) + 360) % 360) as RotationDegrees
}

function nextTransform(current: TransformState, action: TransformAction): TransformState {
  switch (action) {
    case 'rotate-left':
      return { ...current, rotation: normalizeRotation(current.rotation - 90) }
    case 'rotate-right':
      return { ...current, rotation: normalizeRotation(current.rotation + 90) }
    case 'rotate-180':
      return { ...current, rotation: normalizeRotation(current.rotation + 180) }
    case 'flip-horizontal':
      return { ...current, flipHorizontal: !current.flipHorizontal }
    case 'flip-vertical':
      return { ...current, flipVertical: !current.flipVertical }
  }
}

function transformedDimensions(image: LoadedImage, transform: TransformState): { width: number; height: number } {
  if (transform.rotation === 90 || transform.rotation === 270) {
    return { width: image.height, height: image.width }
  }
  return { width: image.width, height: image.height }
}

function actionAnalyticsKey(action: TransformAction): string {
  return action.replaceAll('-', '_')
}

export function RotateImagePage() {
  const t = useT()
  const [image, setImage] = useState<LoadedImage | null>(null)
  const [transform, setTransform] = useState<TransformState>(INITIAL_TRANSFORM)
  const [result, setResult] = useState<RotateResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [dropActive, setDropActive] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const clearResult = useCallback(() => {
    setResult((previous) => {
      if (previous) {
        URL.revokeObjectURL(previous.url)
      }
      return null
    })
  }, [])

  useEffect(() => {
    return () => {
      if (image) {
        URL.revokeObjectURL(image.url)
      }
    }
  }, [image])

  useEffect(() => {
    return () => {
      if (result) {
        URL.revokeObjectURL(result.url)
      }
    }
  }, [result])

  const setLoadedImage = useCallback(
    async (file: File) => {
      setError(null)
      clearResult()

      const mime = fileMime(file)
      if (!mime) {
        setError(t.rotateImage.errorsText.unsupported)
        toolError(UTILITY_KEY, 'unsupported_type')
        return
      }
      if (file.size > MAX_FILE_SIZE) {
        setError(t.rotateImage.errorsText.tooLarge)
        toolError(UTILITY_KEY, 'too_large')
        return
      }

      const url = URL.createObjectURL(file)
      try {
        const decoded = await loadImage(url, t.rotateImage.errorsText)
        const width = decoded.naturalWidth
        const height = decoded.naturalHeight
        if (width < 1 || height < 1 || width * height > MAX_PIXELS) {
          throw new Error(t.rotateImage.errorsText.tooLargeImage)
        }

        const nextImage: LoadedImage = { file, url, width, height, mime }
        setImage((previous) => {
          if (previous) {
            URL.revokeObjectURL(previous.url)
          }
          return nextImage
        })
        setTransform(INITIAL_TRANSFORM)
      } catch (err) {
        URL.revokeObjectURL(url)
        const message = err instanceof Error ? err.message : t.rotateImage.errorsText.corrupt
        setError(message)
        toolError(
          UTILITY_KEY,
          message === t.rotateImage.errorsText.tooLargeImage ? 'too_large_image' : 'corrupt_image',
        )
      }
    },
    [clearResult, t.rotateImage.errorsText],
  )

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      const [file] = Array.from(incoming)
      if (file) {
        void setLoadedImage(file)
      }
    },
    [setLoadedImage],
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        addFiles(event.target.files)
        event.target.value = ''
      }
    },
    [addFiles],
  )

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(true)
  }, [])

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(false)
  }, [])

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      event.stopPropagation()
      setDropActive(false)
      if (event.dataTransfer.files.length > 0) {
        addFiles(event.dataTransfer.files)
      }
    },
    [addFiles],
  )

  const transformToBlob = useCallback(
    async (next: TransformState): Promise<RotateResult> => {
      if (!image) {
        throw new Error(t.rotateImage.errorsText.noImage)
      }

      const decoded = await loadImage(image.url, t.rotateImage.errorsText)
      const dimensions = transformedDimensions(image, next)
      const canvas = document.createElement('canvas')
      canvas.width = dimensions.width
      canvas.height = dimensions.height

      const context = canvas.getContext('2d')
      if (!context) {
        throw new Error(t.rotateImage.errorsText.canvas)
      }

      if (image.mime === 'image/jpeg') {
        context.fillStyle = '#FFFFFF'
        context.fillRect(0, 0, canvas.width, canvas.height)
      }

      context.translate(canvas.width / 2, canvas.height / 2)
      context.rotate((next.rotation * Math.PI) / 180)
      context.scale(next.flipHorizontal ? -1 : 1, next.flipVertical ? -1 : 1)
      context.drawImage(decoded, -image.width / 2, -image.height / 2)

      const blob = await blobFromCanvas(canvas, image.mime, t.rotateImage.errorsText)
      return {
        blob,
        url: URL.createObjectURL(blob),
        width: dimensions.width,
        height: dimensions.height,
        mime: image.mime,
      }
    },
    [image, t.rotateImage.errorsText],
  )

  useEffect(() => {
    if (!image || result) return

    let cancelled = false
    setIsProcessing(true)
    setError(null)
    const timer = window.setTimeout(() => {
      void transformToBlob(transform)
        .then((nextResult) => {
          if (cancelled) {
            URL.revokeObjectURL(nextResult.url)
            return
          }
          setResult(nextResult)
        })
        .catch((err) => {
          if (!cancelled) {
            setError(err instanceof Error ? err.message : t.rotateImage.errorsText.transformFailed)
          }
        })
        .finally(() => {
          if (!cancelled) {
            setIsProcessing(false)
          }
        })
    }, 160)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [image, result, t.rotateImage.errorsText.transformFailed, transform, transformToBlob])

  const handleTransform = useCallback(
    async (action: TransformAction) => {
      if (!image) return

      const next = nextTransform(transform, action)
      const startTime = performance.now()
      const analyticsAction = actionAnalyticsKey(action)
      toolInvoke(UTILITY_KEY, {
        action: analyticsAction,
        rotation: next.rotation,
        flipHorizontal: next.flipHorizontal,
        flipVertical: next.flipVertical,
      })
      setIsProcessing(true)
      setError(null)

      try {
        const nextResult = await transformToBlob(next)
        setTransform(next)
        setResult((previous) => {
          if (previous) {
            URL.revokeObjectURL(previous.url)
          }
          return nextResult
        })
        toolSuccess(UTILITY_KEY, Math.round(performance.now() - startTime), {
          action: analyticsAction,
          rotation: next.rotation,
          flipHorizontal: next.flipHorizontal,
          flipVertical: next.flipVertical,
          mime: image.mime,
        })
      } catch (err) {
        setError(err instanceof Error ? err.message : t.rotateImage.errorsText.transformFailed)
        toolError(UTILITY_KEY, 'transform_failed', {
          action: analyticsAction,
          rotation: next.rotation,
          flipHorizontal: next.flipHorizontal,
          flipVertical: next.flipVertical,
        })
      } finally {
        setIsProcessing(false)
      }
    },
    [image, t.rotateImage.errorsText.transformFailed, transform, transformToBlob],
  )

  const handleDownload = useCallback(() => {
    if (!image || !result) return
    downloadBlob(result.blob, outputFilename(image.file, result.mime))
  }, [image, result])

  return (
    <div className="mx-auto max-w-6xl">
      <ToolHero
        eyebrow={t.rotateImage.eyebrow}
        title={t.rotateImage.title}
        description={t.rotateImage.description}
        chips={[t.rotateImage.formats, t.common.maxFileSize]}
        collapsed={Boolean(image)}
      />

      <div className={cn('grid grid-cols-1 gap-6', image && 'lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start')}>
        <div className="min-w-0 space-y-6">
          {!image && (
            <Card
              className={cn('mx-auto max-w-3xl transition-colors', dropActive && 'border-primary bg-primary/5')}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <CardContent className="p-4 text-center sm:p-8">
                <div className="mx-auto mb-5 hidden h-16 w-16 place-items-center sm:grid rounded-md border bg-background text-sm font-semibold text-muted-foreground">
                  IMG
                </div>
                <p className="mb-2 hidden text-lg font-medium [@media(pointer:fine)]:block">{t.rotateImage.dropTitle}</p>
                <p className="mx-auto mb-5 max-w-md text-sm leading-6 text-muted-foreground">
                  {t.rotateImage.dropDescription}
                </p>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp"
                  className="hidden"
                  onChange={handleInputChange}
                />
                <Button onClick={() => inputRef.current?.click()}>
                  {t.rotateImage.chooseFile}
                </Button>
              </CardContent>
            </Card>
          )}

          {image && (
            <Card className="overflow-hidden">
              <CardContent className="space-y-4 p-4 sm:p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="truncate text-lg font-semibold tracking-normal" title={image.file.name}>
                      {image.file.name}
                    </h2>
                    <div className="mt-2 hidden flex-wrap gap-2 sm:flex">
                      <span className="rounded-md border bg-background px-2 py-1 text-xs font-medium text-muted-foreground">
                        {image.mime.replace('image/', '').toUpperCase()}
                      </span>
                      <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                        {formatSize(image.file.size)}
                      </span>
                      <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                        {image.width} x {image.height}
                      </span>
                    </div>
                  </div>
                  <Button variant="outline" onClick={() => inputRef.current?.click()}>
                    {t.rotateImage.replaceImage}
                  </Button>
                </div>

                <input
                  ref={inputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp"
                  className="hidden"
                  onChange={handleInputChange}
                />

                <div className="grid grid-cols-2 gap-2 sm:gap-4">
                  <PreviewPanel
                    label={t.rotateImage.before}
                    src={image.url}
                    name={image.file.name}
                    size={formatSize(image.file.size)}
                    dimensions={`${image.width} x ${image.height}`}
                  />
                  <PreviewPanel
                    label={t.rotateImage.after}
                    src={result?.url ?? image.url}
                    name={result ? outputFilename(image.file, result.mime) : t.rotateImage.waitingPreview}
                    size={result ? formatSize(result.blob.size) : t.rotateImage.notReady}
                    dimensions={`${result?.width ?? image.width} x ${result?.height ?? image.height}`}
                    muted={!result}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>

        {image && (
          <aside className="min-w-0 space-y-4 lg:sticky lg:top-24">
            <Card>
              <CardContent className="space-y-5 p-5">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold tracking-normal">{t.rotateImage.settings}</h2>
                  <LocalizedProcessingBadge variant="local" />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {t.rotateImage.controls.map((control) => (
                    <Button
                      key={control.action}
                      variant="outline"
                      className={cn(control.action === 'rotate-180' && 'col-span-2')}
                      onClick={() => {
                        void handleTransform(control.action as TransformAction)
                      }}
                      disabled={isProcessing}
                    >
                      {control.label}
                    </Button>
                  ))}
                </div>

                <div className="rounded-md border bg-background p-3 text-sm text-muted-foreground">
                  <div className="flex justify-between gap-3">
                    <span>{t.rotateImage.rotationLabel}</span>
                    <strong className="font-medium text-foreground">{transform.rotation}°</strong>
                  </div>
                  <div className="mt-2 flex justify-between gap-3">
                    <span>{t.rotateImage.flipLabel}</span>
                    <strong className="font-medium text-foreground">
                      {transform.flipHorizontal || transform.flipVertical
                        ? [
                            transform.flipHorizontal ? t.rotateImage.flipHorizontalShort : null,
                            transform.flipVertical ? t.rotateImage.flipVerticalShort : null,
                          ].filter(Boolean).join(', ')
                        : t.rotateImage.none}
                    </strong>
                  </div>
                </div>

                {result && (
                  <Button className="w-full" size="lg" onClick={handleDownload} disabled={isProcessing}>
                    {t.rotateImage.download}
                  </Button>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3 p-5">
                <h2 className="text-lg font-semibold tracking-normal">{t.rotateImage.summary}</h2>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex justify-between gap-3">
                    <span>{t.rotateImage.originalSize}</span>
                    <strong className="font-medium text-foreground">
                      {formatSize(image.file.size)}
                    </strong>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span>{t.rotateImage.originalDimensions}</span>
                    <strong className="font-medium text-foreground">
                      {image.width} x {image.height}
                    </strong>
                  </div>
                  {result && (
                    <div className="flex justify-between gap-3">
                      <span>{t.rotateImage.resultSize}</span>
                      <strong className="font-medium text-foreground">
                        {formatSize(result.blob.size)}
                      </strong>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <p className="rounded-md border bg-card px-3 py-2 text-xs leading-5 text-muted-foreground">
              {t.rotateImage.privacyNote}
            </p>
          </aside>
        )}
      </div>

      <section className="mt-12 grid gap-4 border-t pt-8 lg:grid-cols-3">
        {t.rotateImage.faq.map((item) => (
          <div key={item.question}>
            <h2 className="text-base font-semibold tracking-normal">{item.question}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {item.answer}
            </p>
          </div>
        ))}
      </section>
    </div>
  )
}

function PreviewPanel({
  label,
  src,
  name,
  size,
  dimensions,
  muted = false,
}: {
  label: string
  src: string
  name: string
  size: string
  dimensions: string
  muted?: boolean
}) {
  return (
    <div className="overflow-hidden rounded-md border bg-background">
      <div className={cn('aspect-video bg-muted', muted && 'opacity-55')}>
        <img src={src} alt={name} className="h-full w-full object-contain" />
      </div>
      <div className="space-y-1 p-2 sm:space-y-3 sm:p-3">
        <div>
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="truncate text-sm font-medium" title={name}>
            {name}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="rounded-md border bg-card px-2 py-1 text-xs text-muted-foreground">
            {dimensions}
          </span>
          <span className="rounded-md border bg-card px-2 py-1 text-xs text-muted-foreground">
            {size}
          </span>
        </div>
      </div>
    </div>
  )
}
